// ============================================================================
// ENGAGEMENT FORM MODAL
// ============================================================================
// Add / Edit form modal for the Engagement master.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate -> ConfirmDialog -> loading -> toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Minimize-to-bar support (chevronDown header button) -- multi-instance Add
//
// modal-xl -- fields organized into 2 sections

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import {
  SyutClientSelect,
  SyutEmployeeSelect,
  SyutScopedEmployeeSelect,
  SyutEngagementTypeSelect,
} from 'components/ui/SyutEntitySelect';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { employeeService } from 'api/services/employeeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EngagementFormData {
  code: string;
  clientId: string;
  engagementTypeId: string;
  year: string;
  managerId: string;
  partnerId: string;
  description: string;
  budgetAmount: string;
  startDate: string;
  endDate: string;
  statusCode: string;
}

export interface EngagementFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: EngagementFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: EngagementFormData) => void;
  onClose: () => void;
  // Lookup options passed from the screen
  clientOptions: { value: string; label: string }[];
  engagementTypeOptions: { value: string; label: string }[];
  managerOptions: { value: string; label: string }[];
  partnerOptions: { value: string; label: string }[];
  /** Map of managerId -> partnerId for auto-filling partner when manager is selected */
  managerPartnerMap: Record<string, string>;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const currentYear = new Date().getFullYear();

const INITIAL_FORM_DATA: EngagementFormData = {
  code: '',
  clientId: '',
  engagementTypeId: '',
  year: String(currentYear),
  managerId: '',
  partnerId: '',
  description: '',
  budgetAmount: '',
  startDate: '',
  endDate: '',
  statusCode: 'OPEN',
};

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CODE_PATTERN = /^[A-Z0-9_-]+$/;

/** Generate year options: current year +/- 5 */
const YEAR_OPTIONS: { value: string; label: string }[] = (() => {
  const opts: { value: string; label: string }[] = [];
  for (let y = currentYear - 5; y <= currentYear + 5; y++) {
    opts.push({ value: String(y), label: String(y) });
  }
  return opts;
})();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EngagementFormModal: React.FC<EngagementFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  clientOptions,
  engagementTypeOptions,
  managerOptions,
  partnerOptions,
  managerPartnerMap,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<EngagementFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors]
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: EngagementFormData = src ? { ...src } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.code?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.code || 'Engagement'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Field setter helper ---
  const setField = useCallback(
    (field: keyof EngagementFormData, value: string) => {
      clearFieldError(field);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearFieldError]
  );

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();

    // Code — optional in create mode (auto-generated by backend), validated only if provided
    if (code && code.length > 50) {
      errors.code = 'Code must be 50 characters or less';
    } else if (code && !CODE_PATTERN.test(code)) {
      errors.code = 'Code: uppercase letters, numbers, hyphens only';
    }

    // Client
    if (!formData.clientId) {
      errors.clientId = 'Client is required';
    }

    // Engagement Type
    if (!formData.engagementTypeId) {
      errors.engagementTypeId = 'Engagement type is required';
    }

    // Year
    if (!formData.year) {
      errors.year = 'Year is required';
    }

    // Manager
    if (!formData.managerId) {
      errors.managerId = 'Manager is required';
    }

    // Description
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.trim().length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }

    // End date >= Start date
    if (formData.startDate && formData.endDate) {
      if (formData.endDate < formData.startDate) {
        errors.endDate = 'End date must be on or after start date';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(
          `[data-field="${firstField}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea, select')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save -> delegate to parent ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      description: formData.description.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Engagement' : 'Edit Engagement'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          {/* ============================================================ */}
          {/* Section 1: Engagement Details                                 */}
          {/* ============================================================ */}

          {/* Row 1: Code + Year */}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">Code</label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={50}
                placeholder={mode === 'create' ? 'Auto-generated (ENG-YYYY-NNN)' : ''}
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => {
                  clearFieldError('code');
                  setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                }}
              />
              {mode === 'create' && !getFieldError('code') && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                  Format hint: ENG-YYYY-NNN
                </div>
              )}
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            <div className="form-group" data-field="year">
              <SyutSelect
                label={<>Year <span className="text-danger">*</span></>}
                value={formData.year}
                options={YEAR_OPTIONS}
                isClearable={false}
                size="sm"
                placeholder="Select year..."
                error={getFieldError('year')}
                onChange={(val) => {
                  clearFieldError('year');
                  setFormData((prev) => ({ ...prev, year: (val as string) || '' }));
                }}
              />
            </div>
          </div>

          {/* Row 2: Client + Engagement Type */}
          <div className="form-grid-2">
            <div className="form-group" data-field="clientId">
              <SyutClientSelect
                label={<>Client <span className="text-danger">*</span></>}
                value={formData.clientId}
                seedOptions={clientOptions}
                error={getFieldError('clientId')}
                onChange={(val) => {
                  clearFieldError('clientId');
                  setFormData((prev) => ({ ...prev, clientId: val }));
                }}
              />
            </div>

            <div className="form-group" data-field="engagementTypeId">
              <SyutEngagementTypeSelect
                label={<>Engagement Type <span className="text-danger">*</span></>}
                value={formData.engagementTypeId}
                seedOptions={engagementTypeOptions}
                error={getFieldError('engagementTypeId')}
                onChange={(val) => {
                  clearFieldError('engagementTypeId');
                  setFormData((prev) => ({ ...prev, engagementTypeId: val }));
                }}
              />
            </div>
          </div>

          {/* Row 3: Manager + Partner */}
          <div className="form-grid-2">
            <div className="form-group" data-field="managerId">
              <SyutScopedEmployeeSelect
                label={<>Manager <span className="text-danger">*</span></>}
                scopeMode="action"
                moduleId="engagement-management"
                role="manager"
                value={formData.managerId}
                seedOptions={managerOptions}
                error={getFieldError('managerId')}
                onChange={async (newManagerId) => {
                  clearFieldError('managerId');
                  // Auto-fill partner from the selected manager's partner.
                  // Check the preloaded map first; fall back to getById for managers
                  // not in the preloaded list.
                  let mgrPartnerId = '';
                  if (newManagerId) {
                    mgrPartnerId = managerPartnerMap[newManagerId] || '';
                    if (!mgrPartnerId) {
                      try {
                        const empRes = await employeeService.getById(newManagerId);
                        mgrPartnerId = (empRes?.data as any)?.partnerId || '';
                      } catch { /* ignore — user can pick partner manually */ }
                    }
                  }
                  setFormData((prev) => ({
                    ...prev,
                    managerId: newManagerId,
                    partnerId: newManagerId ? mgrPartnerId : '',
                  }));
                }}
              />
            </div>

            <div className="form-group" data-field="partnerId">
              <SyutEmployeeSelect
                label="Partner (default from manager)"
                value={formData.partnerId || undefined}
                role="partner"
                seedOptions={partnerOptions}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, partnerId: val }));
                }}
              />
            </div>
          </div>

          {/* Row 4: Description */}
          <div className="form-group" data-field="description">
            <label className="form-label">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-input${getFieldError('description') ? ' form-field-error' : ''}`}
              value={formData.description}
              rows={3}
              maxLength={500}
              placeholder="Describe the engagement scope and objectives"
              disabled={isSaving}
              onChange={(e) => setField('description', e.target.value)}
              style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.15rem' }}>
              {getFieldError('description') ? (
                <div className="form-error-message" style={{ margin: 0 }}>{getFieldError('description')}</div>
              ) : <span />}
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {formData.description.length}/500
              </span>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 2: Budget & Schedule                                  */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Budget & Schedule
          </h4>

          {/* Row 1: Budget Amount + Start Date */}
          <div className="form-grid-2">
            <div className="form-group" data-field="budgetAmount">
              <label className="form-label">Budget Amount</label>
              <input
                type="number"
                className="form-input"
                value={formData.budgetAmount}
                step="0.01"
                min="0"
                placeholder="0.00"
                disabled={isSaving}
                onChange={(e) => setField('budgetAmount', e.target.value)}
              />
            </div>

            <div className="form-group" data-field="startDate">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.startDate}
                disabled={isSaving}
                onChange={(e) => setField('startDate', e.target.value)}
              />
            </div>
          </div>

          {/* Row 2: End Date + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="endDate">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className={`form-input${getFieldError('endDate') ? ' form-field-error' : ''}`}
                value={formData.endDate}
                disabled={isSaving}
                onChange={(e) => setField('endDate', e.target.value)}
              />
              {getFieldError('endDate') && (
                <div className="form-error-message">{getFieldError('endDate')}</div>
              )}
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as string) || 'OPEN',
                  }));
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create engagement "${formData.code.trim()}"?`
            : `Save changes to "${formData.code.trim()}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default EngagementFormModal;
